import {
  FC,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { api, Id } from "@/smart-views/client/api";
import { EditableTitle } from "./EditableTitle";
import { BlockMenu } from "./BlockMenu";
import { BlockLightbox } from "./BlockLightbox";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSPopover,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { EditPromptModal } from "../EditPromptModal";
import { SourcesPanel } from "../citations/SourcesPanel";
import { dedupeSourceIds } from "../citations/source-utils";
import { commentOnSmartViewBlock } from "@/smart-views/client/backend-api";
import { logger } from "@/modules/logger";

interface BlockShellProps {
  smartViewId: string;
  blockId: Id<"blocks">;
  title: string;
  prompt: string;
  iconKind: MDSIconKind;
  updatedAt: number;
  isCollapsed?: boolean;
  sources?: string[];
  isReadOnly?: boolean;
  dragHandleProps?: HTMLAttributes<HTMLDivElement> & { draggable?: boolean };
  isDragging?: boolean;
  isUpdated?: boolean;
  commentWorkItems?: BlockStatusWorkItem[];
  sourceSyncWorkItems?: BlockStatusWorkItem[];
  children: ReactNode;
}

export interface BlockStatusWorkItem {
  id: string;
  status: "pending" | "running" | "resolved" | "failed";
  content: string;
  errorMessage?: string;
  createdAt: number;
}

const pendingCollapsedStateByBlockId = new Map<string, boolean>();

export const BlockShell: FC<BlockShellProps> = ({
  smartViewId,
  blockId,
  title,
  prompt,
  iconKind,
  updatedAt,
  isCollapsed = false,
  sources = [],
  isReadOnly = false,
  dragHandleProps,
  isDragging = false,
  isUpdated = false,
  commentWorkItems = [],
  sourceSyncWorkItems = [],
  children,
}) => {
  const updateTitle = useMutation(api.smartViews.client.updateBlockTitle);
  const updatePrompt = useMutation(api.smartViews.client.updateBlockPrompt);
  const setCollapsed = useMutation(api.smartViews.client.setBlockCollapsed);
  const deleteBlock = useMutation(api.smartViews.client.deleteBlock);
  const restoreBlockVersion = useMutation(api.smartViews.client.restoreBlockVersion);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSending, setCommentSending] = useState(false);
  const commentButtonRef = useRef<HTMLButtonElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const commentComposerId = useId();
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [blockMenuOpen, setBlockMenuOpen] = useState(false);
  const [optimisticCollapsed, setOptimisticCollapsed] = useState<boolean | null>(
    () => pendingCollapsedStateByBlockId.get(blockId) ?? null
  );
  const canDrag = !isReadOnly && Boolean(dragHandleProps);
  const uniqueSourceIds = dedupeSourceIds(sources);
  const effectiveIsCollapsed = optimisticCollapsed ?? isCollapsed;

  useEffect(() => {
    if (commenting) commentRef.current?.focus();
  }, [commenting]);

  useEffect(() => {
    const pendingCollapsed = pendingCollapsedStateByBlockId.get(blockId);
    if (pendingCollapsed !== isCollapsed) return;

    pendingCollapsedStateByBlockId.delete(blockId);
    setOptimisticCollapsed(current => (current === pendingCollapsed ? null : current));
  }, [blockId, isCollapsed]);

  useEffect(() => {
    if (!effectiveIsCollapsed) return;
    setCommenting(false);
    setSourcesOpen(false);
    setLightboxOpen(false);
    setBlockMenuOpen(false);
  }, [effectiveIsCollapsed]);

  const toggleCollapsed = () => {
    const nextIsCollapsed = !effectiveIsCollapsed;
    pendingCollapsedStateByBlockId.set(blockId, nextIsCollapsed);
    setOptimisticCollapsed(nextIsCollapsed);
    setCollapsed({ blockId, isCollapsed: nextIsCollapsed }).catch(err => {
      if (pendingCollapsedStateByBlockId.get(blockId) === nextIsCollapsed) {
        pendingCollapsedStateByBlockId.delete(blockId);
      }
      setOptimisticCollapsed(current => (current === nextIsCollapsed ? null : current));
      logger.error({
        message: "Smart View: failed to update block collapsed state",
        info: { blockId, err: String(err) },
      });
    });
  };

  const toggleCommentComposer = () => {
    setCommenting(isOpen => !isOpen);
  };

  const setCommentPopoverOpen = (isOpen: boolean) => {
    setCommenting(isOpen);
  };

  const closeCommentComposer = () => {
    setCommenting(false);
    setComment("");
  };

  const submitComment = async () => {
    const trimmed = comment.trim();
    if (!trimmed || commentSending) return;
    setCommentSending(true);
    try {
      await commentOnSmartViewBlock({
        smartViewId,
        blockId,
        content: trimmed,
      });
      setComment("");
      setCommenting(false);
    } catch (err) {
      logger.error({
        message: "Smart View: failed to post block comment",
        info: { err: String(err) },
      });
    } finally {
      setCommentSending(false);
    }
  };

  const onCommentKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submitComment();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeCommentComposer();
    }
  };

  return (
    <Card
      $isDragging={isDragging}
      $isUpdated={isUpdated}
      $isCollapsed={effectiveIsCollapsed}
      data-smart-view-block-card
      data-collapsed={effectiveIsCollapsed ? "true" : undefined}
    >
      <Header $isDraggable={canDrag} $isCollapsed={effectiveIsCollapsed} {...dragHandleProps}>
        <TitleRow>
          {!effectiveIsCollapsed && (
            <IconSlot>
              <BlockIcon
                className={canDrag ? "block-icon block-icon-hidden-on-drag" : "block-icon"}
                kind={iconKind}
              />
              {canDrag && (
                <DragHandle className="block-drag-handle" aria-hidden="true">
                  <MDSIcon kind={MDSIconKind.Grip} />
                </DragHandle>
              )}
            </IconSlot>
          )}
          <EditableTitle
            value={title}
            onSave={next => updateTitle({ blockId, title: next })}
            placeholder="Untitled block"
            disabled={isReadOnly}
          />
          {!effectiveIsCollapsed && (
            <BlockStatusBadges
              commentWorkItems={commentWorkItems}
              sourceSyncWorkItems={sourceSyncWorkItems}
            />
          )}
        </TitleRow>
        {!isReadOnly && (
          <MenuSlot
            className="block-menu-slot"
            data-commenting={commenting ? "true" : undefined}
            data-menu-open={blockMenuOpen ? "true" : undefined}
          >
            {!effectiveIsCollapsed && (
              <>
                <MDSTooltip
                  config={{
                    label: commenting ? "Hide comment" : "Comment",
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSIconButton
                    ref={commentButtonRef}
                    size={MDSIconButtonSize.XSmall}
                    iconKind={MDSIconKind.Comment}
                    variant={MDSIconButtonVariant.OnChrome}
                    onClick={toggleCommentComposer}
                    isToggled={commenting}
                    aria-controls={commenting ? commentComposerId : undefined}
                    aria-expanded={commenting}
                    aria-label="Comment on block"
                    rounded
                  />
                </MDSTooltip>
                <MDSTooltip config={{ label: "Expand", placement: MDSTooltipPlacement.Top }}>
                  <MDSIconButton
                    size={MDSIconButtonSize.XSmall}
                    iconKind={MDSIconKind.Expand}
                    variant={MDSIconButtonVariant.OnChrome}
                    onClick={() => setLightboxOpen(true)}
                    rounded
                  />
                </MDSTooltip>
              </>
            )}
            <BlockMenu
              blockId={blockId}
              updatedAt={updatedAt}
              isCollapsed={effectiveIsCollapsed}
              onToggleCollapsed={toggleCollapsed}
              onEditPrompt={() => setPromptModalOpen(true)}
              onDelete={() => deleteBlock({ blockId })}
              onRestoreVersion={versionId => restoreBlockVersion({ blockId, versionId })}
              onOpenChange={setBlockMenuOpen}
            />
          </MenuSlot>
        )}
      </Header>
      {!isReadOnly && !effectiveIsCollapsed && (
        <MDSPopover
          styling={false}
          triggerRef={commentButtonRef}
          isOpen={commenting}
          onOpenChange={setCommentPopoverOpen}
          placement="bottom end"
          offset={8}
          containerPadding={12}
          isNonModal
        >
          <CommentComposer
            id={commentComposerId}
            role="dialog"
            aria-label="Block comment"
            onPointerDown={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
          >
            <CommentTextarea
              ref={commentRef}
              value={comment}
              placeholder="Tell Mem how this block should change..."
              onChange={e => setComment(e.target.value)}
              onKeyDown={onCommentKeyDown}
              readOnly={commentSending}
              rows={2}
            />
            <CommentActions>
              <MDSTooltip config={{ label: "Cancel", placement: MDSTooltipPlacement.Top }}>
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MDSIconKind.Exit}
                  variant={MDSIconButtonVariant.OnChrome}
                  onClick={closeCommentComposer}
                  aria-label="Cancel comment"
                  rounded
                />
              </MDSTooltip>
              <MDSTooltip config={{ label: "Send", placement: MDSTooltipPlacement.Top }}>
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MDSIconKind.Send}
                  variant={MDSIconButtonVariant.OnEmphasis}
                  onClick={() => void submitComment()}
                  isDisabled={!comment.trim() || commentSending}
                  isLoading={commentSending}
                  aria-label="Send comment"
                  rounded
                />
              </MDSTooltip>
            </CommentActions>
          </CommentComposer>
        </MDSPopover>
      )}
      {!effectiveIsCollapsed && <Body data-smart-view-block-body>{children}</Body>}
      {!effectiveIsCollapsed && uniqueSourceIds.length > 0 && (
        <SourcesFooter>
          <SourcesToggle
            type="button"
            aria-expanded={sourcesOpen}
            onClick={() => setSourcesOpen(isOpen => !isOpen)}
          >
            <MDSIcon
              kind={MDSIconKind.Document}
              size={11}
              boxSize={16}
              color={MDSIconColor.Inherit}
            />
            <SourcesToggleLabel>Sources</SourcesToggleLabel>
            <SourcesCount>
              {uniqueSourceIds.length} note{uniqueSourceIds.length === 1 ? "" : "s"}
            </SourcesCount>
            <Caret $open={sourcesOpen}>
              <MDSIcon
                kind={MDSIconKind.AngleRight}
                size={11}
                boxSize={12}
                color={MDSIconColor.Inherit}
              />
            </Caret>
          </SourcesToggle>
          <SourcesPanel noteIds={uniqueSourceIds} isOpen={sourcesOpen} />
        </SourcesFooter>
      )}
      <EditPromptModal
        isOpen={promptModalOpen}
        scopeLabel="this block"
        initialValue={prompt}
        onClose={() => setPromptModalOpen(false)}
        onSave={async next => {
          await updatePrompt({ blockId, prompt: next });
        }}
      />
      <BlockLightbox
        isOpen={lightboxOpen}
        title={title}
        iconKind={iconKind}
        onClose={() => setLightboxOpen(false)}
      >
        {children}
        {uniqueSourceIds.length > 0 && (
          <LightboxSources>
            <SourcesPanel noteIds={uniqueSourceIds} isOpen={true} />
          </LightboxSources>
        )}
      </BlockLightbox>
    </Card>
  );
};

const BlockStatusBadges: FC<{
  commentWorkItems: BlockStatusWorkItem[];
  sourceSyncWorkItems: BlockStatusWorkItem[];
}> = ({ commentWorkItems, sourceSyncWorkItems }) => {
  const hoverCardId = useId();
  const pendingCommentCount = commentWorkItems.length;
  const sourceSyncStatus = getSourceSyncStatus(sourceSyncWorkItems);

  if (pendingCommentCount === 0 && sourceSyncStatus === null) return null;

  return (
    <StatusBadges
      draggable={false}
      onPointerDown={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onDragStart={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {pendingCommentCount > 0 && (
        <StatusBadgeTrigger
          tabIndex={0}
          draggable={false}
          aria-describedby={hoverCardId}
          aria-label={`${pendingCommentCount} open comment${pendingCommentCount === 1 ? "" : "s"}`}
        >
          <StatusBadge $tone="comment">
            <MDSIcon
              kind={MDSIconKind.Comment}
              size={11}
              boxSize={14}
              color={MDSIconColor.Inherit}
            />
            <StatusBadgeCount>{pendingCommentCount}</StatusBadgeCount>
          </StatusBadge>
        </StatusBadgeTrigger>
      )}
      {sourceSyncStatus === "checking" && (
        <StatusBadgeTrigger
          tabIndex={0}
          draggable={false}
          aria-describedby={hoverCardId}
          aria-label="Propagating updates to source notes"
        >
          <StatusBadge $tone="quiet">
            <MDSIcon
              kind={MDSIconKind.SpinnerThird}
              size={11}
              boxSize={14}
              color={MDSIconColor.Inherit}
            />
          </StatusBadge>
        </StatusBadgeTrigger>
      )}
      {sourceSyncStatus === "needsReview" && (
        <StatusBadgeTrigger
          tabIndex={0}
          draggable={false}
          aria-describedby={hoverCardId}
          aria-label="Source update needs review"
        >
          <StatusBadge $tone="warning">
            <MDSIcon
              kind={MDSIconKind.Exclamation}
              size={11}
              boxSize={14}
              color={MDSIconColor.Inherit}
            />
          </StatusBadge>
        </StatusBadgeTrigger>
      )}
      <StatusHoverCard id={hoverCardId} role="status" data-status-hover-card>
        {commentWorkItems.length > 0 && (
          <StatusHoverSection>
            <StatusHoverHeader>
              <StatusHoverIcon $tone="comment">
                <MDSIcon
                  kind={MDSIconKind.Comment}
                  size={11}
                  boxSize={14}
                  color={MDSIconColor.Inherit}
                />
              </StatusHoverIcon>
              <StatusHoverTitle>
                {commentWorkItems.length} open comment
                {commentWorkItems.length === 1 ? "" : "s"}
              </StatusHoverTitle>
            </StatusHoverHeader>
            <StatusHoverItems>
              {commentWorkItems.slice(0, 3).map(item => (
                <StatusHoverItem key={item.id}>
                  <StatusHoverItemText>{item.content}</StatusHoverItemText>
                  <StatusHoverMeta>{workItemStatusLabel(item)}</StatusHoverMeta>
                </StatusHoverItem>
              ))}
              {commentWorkItems.length > 3 && (
                <StatusHoverMore>+{commentWorkItems.length - 3} more</StatusHoverMore>
              )}
            </StatusHoverItems>
          </StatusHoverSection>
        )}
        {sourceSyncWorkItems.length > 0 && (
          <StatusHoverSection>
            <StatusHoverHeader>
              <StatusHoverIcon $tone={sourceSyncStatus === "needsReview" ? "warning" : "quiet"}>
                <MDSIcon
                  kind={
                    sourceSyncStatus === "needsReview"
                      ? MDSIconKind.Exclamation
                      : MDSIconKind.SpinnerThird
                  }
                  size={11}
                  boxSize={14}
                  color={MDSIconColor.Inherit}
                />
              </StatusHoverIcon>
              <StatusHoverTitle>
                {sourceSyncStatus === "needsReview"
                  ? "Source update needs review"
                  : getSourceSyncTitle(sourceSyncWorkItems)}
              </StatusHoverTitle>
            </StatusHoverHeader>
            <StatusHoverItems>
              {sourceSyncWorkItems.slice(0, 3).map(item => (
                <StatusHoverItem key={item.id}>
                  <StatusHoverItemText>{item.content}</StatusHoverItemText>
                  <StatusHoverMeta>
                    {item.status === "failed" && item.errorMessage
                      ? item.errorMessage
                      : workItemStatusLabel(item)}
                  </StatusHoverMeta>
                </StatusHoverItem>
              ))}
              {sourceSyncWorkItems.length > 3 && (
                <StatusHoverMore>+{sourceSyncWorkItems.length - 3} more</StatusHoverMore>
              )}
            </StatusHoverItems>
          </StatusHoverSection>
        )}
      </StatusHoverCard>
    </StatusBadges>
  );
};

const getSourceSyncStatus = (items: BlockStatusWorkItem[]): "checking" | "needsReview" | null => {
  if (items.length === 0) return null;
  return items.some(item => item.status === "failed") ? "needsReview" : "checking";
};

const workItemStatusLabel = (item: BlockStatusWorkItem): string => {
  if (item.status === "failed") return "Needs review";
  if (item.status === "running") return "Mem is working on this";
  return "Waiting for Mem";
};

const getSourceSyncTitle = (items: BlockStatusWorkItem[]): string => {
  const content = items[0]?.content;
  if (content?.startsWith("Propagating updates to ")) {
    return content.split(":")[0] ?? "Propagating updates to source notes";
  }

  return "Propagating updates to source notes";
};

const CommentComposer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
  width: min(360px, calc(100vw - 24px));
  padding: ${({ theme }) => theme.spacing["2"]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.popover};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CommentTextarea = styled.textarea`
  min-width: 0;
  min-height: 38px;
  border: none;
  background: transparent;
  outline: none;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;
  color: inherit;
  resize: none;
  padding: 2px 0;
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
`;

const Card = styled.div<{
  $isDragging: boolean;
  $isUpdated: boolean;
  $isCollapsed: boolean;
}>`
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing["5"]};
  background: ${({ theme, $isUpdated }) =>
    $isUpdated ? theme.colors.background.banner.emphasis : theme.colors.surface.pane};
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $isCollapsed }) => ($isCollapsed ? "0" : theme.spacing["4"])};
  position: relative;
  height: ${({ $isCollapsed }) => ($isCollapsed ? "auto" : "100%")};
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 1px 8px rgba(0, 0, 0, 0.02);
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  transition:
    opacity 0.15s ease,
    box-shadow 0.15s ease;

  .block-menu-slot {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .block-menu-slot[data-commenting="true"],
  .block-menu-slot[data-menu-open="true"] {
    opacity: 1;
  }

  .block-drag-handle {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .block-icon {
    opacity: 1;
    transition: opacity 0.15s ease;
  }

  &:hover .block-menu-slot,
  &:focus-within .block-menu-slot,
  &:hover .block-drag-handle,
  &:focus-within .block-drag-handle {
    opacity: 1;
  }

  &:hover .block-icon-hidden-on-drag,
  &:focus-within .block-icon-hidden-on-drag {
    opacity: 0;
  }
`;

const MenuSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const LightboxSources = styled.div`
  margin-top: ${({ theme }) => theme.spacing["4"]};
  padding-top: ${({ theme }) => theme.spacing["3"]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const Header = styled.div<{ $isDraggable: boolean; $isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  cursor: ${({ $isDraggable }) => ($isDraggable ? "grab" : "default")};
  padding-bottom: ${({ theme, $isCollapsed }) => ($isCollapsed ? "0" : theme.spacing["3"])};
  border-bottom: ${({ theme, $isCollapsed }) =>
    $isCollapsed ? "none" : `1px solid ${theme.colors.border.default}`};

  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? "grabbing" : "default")};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  flex: 1;
  min-width: 0;
`;

const StatusBadges = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  z-index: 2;
  cursor: default;

  &:hover [data-status-hover-card],
  &:focus-within [data-status-hover-card] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const StatusBadgeTrigger = styled.span`
  display: inline-flex;
  border-radius: 999px;
  outline: none;
  cursor: default;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const StatusBadge = styled.span<{ $tone: "comment" | "quiet" | "warning" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 19px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === "comment"
        ? theme.colors.border.emphasis
        : $tone === "warning"
          ? theme.colors.border.input.default.danger
          : theme.colors.border.default};
  color: ${({ theme, $tone }) =>
    $tone === "comment"
      ? theme.colors.text.emphasis
      : $tone === "warning"
        ? theme.colors.text.danger
        : theme.colors.text.tertiary};
  background: ${({ theme, $tone }) =>
    $tone === "comment"
      ? theme.colors.background.badge.emphasis
      : $tone === "warning"
        ? theme.colors.background.badge.danger
        : theme.colors.background.button.onChrome.default};
  font-size: 11px;
  line-height: 1;
`;

const StatusBadgeCount = styled.span`
  font-weight: 600;
`;

const StatusHoverCard = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  width: min(320px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  padding: ${({ theme }) => theme.spacing["3"]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.popover};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0;
  transform: translateY(-3px);
  pointer-events: none;
  cursor: default;
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 8px;
  }
`;

const StatusHoverSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StatusHoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StatusHoverIcon = styled.span<{ $tone: "comment" | "quiet" | "warning" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  color: ${({ theme, $tone }) =>
    $tone === "comment"
      ? theme.colors.text.emphasis
      : $tone === "warning"
        ? theme.colors.text.danger
        : theme.colors.text.tertiary};
  background: ${({ theme, $tone }) =>
    $tone === "comment"
      ? theme.colors.background.badge.emphasis
      : $tone === "warning"
        ? theme.colors.background.badge.danger
        : theme.colors.background.badge.default};
`;

const StatusHoverTitle = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
`;

const StatusHoverItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StatusHoverItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const StatusHoverItemText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const StatusHoverMeta = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  line-height: 1.25;
  word-break: break-word;
`;

const StatusHoverMore = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  line-height: 1.25;
`;

const IconSlot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 18px;
  width: 18px;

  .block-icon {
    pointer-events: none;
  }
`;

const DragHandle = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const BlockIcon = styled(MDSIcon)`
  color: ${({ theme }) => theme.colors.text.secondary};
  position: absolute;
  inset: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const SourcesFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["3"]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  margin-top: 0;
`;

const SourcesToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 2px 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  align-self: flex-start;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SourcesToggleLabel = styled.span`
  font-weight: 500;
`;

const SourcesCount = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  opacity: 0.7;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
`;
